import { useState } from 'react';
import { Select, Spinner, TextArea, TextField } from '@cimpress/react-components';
import type { SelectorOption } from './types';

interface CustomSelectorProps<T extends string | undefined> {
    selectorLabel?: string;
    textfieldLabel?: string;
    data: SelectorOption[];
    value: T;
    setValue: (value: T) => void;
    validator: (value: string) => boolean;
    isLoading?: boolean;
    rows?: number;
    /**
     * When true, the input field is always visible regardless of selection.
     * When false (default), input field only shows when "Custom" is selected or value not in dropdown.
     *
     * Set to true for developer/playground pages where URL visibility is important.
     * Keep false (default) for end-user pages to maintain clean UI.
     */
    alwaysShowInput?: boolean;
}

const CUSTOM_LABEL = 'Custom';

export function CustomSelector<T extends string | undefined>({
    selectorLabel,
    textfieldLabel,
    data,
    value,
    setValue,
    validator = () => true,
    isLoading = false,
    rows,
    alwaysShowInput = false,
}: CustomSelectorProps<T>) {
    const customOption: SelectorOption = { label: CUSTOM_LABEL, value: '' };
    const dataWithCustomOption: SelectorOption[] = [customOption, ...data];

    const indexOfCurrentValue = dataWithCustomOption.findIndex((d) => d.value === value);
    const initialState = dataWithCustomOption[indexOfCurrentValue] || customOption;

    const [selection, setSelection] = useState(initialState);
    const [showInputField, setShowInputField] = useState(indexOfCurrentValue <= 0);

    const onChangeSelect = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }

        setSelection(selected);

        if (selected.label !== customOption.label) {
            setValue(selected.value as T);
        } else {
            setValue(value);
        }

        setShowInputField(selected.label === CUSTOM_LABEL);
    };

    return (
        <>
            {isLoading && <Spinner />}
            {!isLoading && (
                <Select
                    onChange={onChangeSelect}
                    value={selection}
                    options={dataWithCustomOption}
                    label={selectorLabel}
                />
            )}

            {(alwaysShowInput || showInputField) && !rows && (
                <TextField
                    label={textfieldLabel}
                    value={value}
                    onChange={(e) => setValue(e.target.value as T)}
                    status={!value || validator(value) ? undefined : 'error'}
                />
            )}
            {(alwaysShowInput || showInputField) && rows && (
                <TextArea
                    label={textfieldLabel}
                    value={value}
                    rows={rows}
                    onChange={(e) => setValue(e.target.value as T)}
                    status={!value || validator(value) ? undefined : 'error'}
                />
            )}
        </>
    );
}
